import React, { useEffect, useState } from "react";
import useUserStore from "../../../stores/user";
import supabase from "../../../utils/supabase";

interface AdminInformationProps {
  className?: string;
}

const AdminInformation: React.FC<AdminInformationProps> = ({
  className = "",
}) => {
  const user = useUserStore((state) => state.user);
  const profile = useUserStore((state) => state.profile);
  const [adminDistrict, setAdminDistrict] = useState<any>(null);
  const [districtSchools, setDistrictSchools] = useState<any[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !profile) return;

    async function loadAdminData() {
      await Promise.all([loadAdminDistrict(), loadDistrictSchools()]);
      setLoading(false);
    }

    loadAdminData();
  }, [user?.id, profile]);

  async function loadAdminDistrict() {
    if (!profile.district) {
      setAdminDistrict(null);
      return;
    }

    const { data, error } = await supabase
      .from("districts")
      .select("*")
      .eq("id", profile.district)
      .maybeSingle();

    if (error) {
      console.error("Error loading admin district:", error);
      setAdminDistrict(null);
    } else {
      setAdminDistrict(data);
    }
  }

  async function loadDistrictSchools() {
    if (!profile.district) {
      setDistrictSchools([]);
      return;
    }

    const { data, error } = await supabase
      .from("schools")
      .select("*")
      .eq("district_id", profile.district)
      .order("name");

    if (error) {
      console.error("Error loading district schools:", error);
      setDistrictSchools([]);
      setSelectedSchool("");
    } else {
      const schools = data || [];
      setDistrictSchools(schools);
      // Set the first school as selected if schools exist
      if (schools.length > 0) {
        setSelectedSchool(schools[0].id);
      } else {
        setSelectedSchool("");
      }
    }
  }

  if (loading) {
    return (
      <div className={`bg-white shadow rounded-lg p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-3">
                <div className="h-5 w-5 bg-gray-200 rounded"></div>
                <div className="flex-1">
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white shadow rounded-lg p-6 ${className}`}>
      <h2 className="text-lg font-medium text-gray-900 mb-4">My Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">District</p>
            <p className="text-sm text-gray-900">
              {adminDistrict ? adminDistrict.name : "No district assigned"}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-500">
              Schools in District
            </p>
            {!adminDistrict ? (
              <p className="text-sm text-gray-500 mt-1">No district assigned</p>
            ) : districtSchools.length > 0 ? (
              <select
                value={selectedSchool}
                onChange={(e) => setSelectedSchool(e.target.value)}
                className="mt-1 block w-auto pl-3 pr-10 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                {districtSchools.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-gray-500 mt-1">
                No schools in district
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInformation;
